import {
  ADD_BAGS_USED_FOR_ORDER,
  CHECK_MASTER_RUN,
  GET_RUN_FOR_SEA_SPIDER,
} from '@buncha/config/graphQL/documentNode/queries';
import {
  CheckMasterRun,
  CheckMasterRunResponse,
} from '../types/NavigateVanLoadTypes';
import {
  DeliveryLocationStatus,
  NavigateVanLoadContextState,
  SeaSpiderRun,
} from '../types';
import {Dispatch, useCallback, useContext, useReducer, useState} from 'react';
import {NavigateVanLoadContext, initialState} from '../context/NavigateVanLoad';
import {PayloadAction, createSlice} from '@reduxjs/toolkit';

import {ApiState} from '@buncha/common/types/common';
import {ToastType} from '@buncha/components/composites/notification/type';
import {gqlService} from '@buncha/config/graphQL';
import {useToastMessage} from '@buncha/components/composites/notification';

const reducer = {
  setSeaSpiderRun: function (
    state: NavigateVanLoadContextState,
    action: PayloadAction<SeaSpiderRun | undefined>,
  ) {
    state.run = action.payload;
    state.loading = false;
  },
  setUsedBagCount: function (
    state: NavigateVanLoadContextState,
    action: PayloadAction<{orderId: number; usedBag: number}>,
  ) {
    const run = state?.run;
    const payload = action.payload;
    const orderId = payload?.orderId;
    const usedBag = payload?.usedBag;
    if (run) {
      const order = run.ordersForShopper.find(ord => ord.id === orderId);
      if (order) order.bagsUsed = usedBag;
    }
  },
  setBagCountUpdateStatus: function (
    state: NavigateVanLoadContextState,
    action: PayloadAction<{orderId: number; isBagCountUpdated: boolean}>,
  ) {
    const payload = action.payload;
    const orderId = payload.orderId;

    const update = state.bagCountUpdateStatus;

    update[orderId] = payload.isBagCountUpdated;
  },
};

const slice = createSlice({
  initialState,
  name: 'navigateVanLoad',
  reducers: reducer,
});

export function useNavigateVanLoadReducer() {
  const navigateVanLoadReducer = useReducer(slice.reducer, initialState);
  return navigateVanLoadReducer;
}

export function useNavigateVanLoadContext() {
  return useContext(NavigateVanLoadContext);
}

export const navigateVanLoadActions = slice.actions;

export function useGetRunforSeaSpider(dispatch: Dispatch<any> | null) {
  const [fetchingRuns, setFetchingRuns] = useState(false);
  const getRunForSeaSpider = async (runId: number) => {
    if (!runId) return;
    setFetchingRuns(true);
    try {
      const response = await gqlService?.query({
        query: GET_RUN_FOR_SEA_SPIDER,
        variables: {runId: runId},
        fetchPolicy: 'network-only',
      });
      if (response?.data) {
        const run = response?.data?.getRun;

        if (dispatch) dispatch(navigateVanLoadActions.setSeaSpiderRun(run));
        setFetchingRuns(false);
        return run;
      }
      setFetchingRuns(false);
    } catch (err: any) {
      setFetchingRuns(false);
    }
  };

  return {fetchingRuns, getRunForSeaSpider};
}

export function useAddBagsUsedForOrder(dispatch: Dispatch<any> | null) {
  const [loading, setLoading] = useState(false);
  const addBagsUsersForOrder = async (
    orderId: number,
    bagsUsed: number,
    runId?: number,
  ) => {
    try {
      setLoading(true);
      let paramsToSend: any = {
        orderId: orderId,
        bagsUsed: bagsUsed,
      };
      if (runId)
        paramsToSend = {
          ...paramsToSend,
          runId: runId,
        };

      const res = await gqlService?.mutation({
        mutation: ADD_BAGS_USED_FOR_ORDER,
        fetchPolicy: 'network-only',
        variables: paramsToSend,
      });

      const orderRes = res?.data?.addBagsUsedForOrder;

      if (orderRes && dispatch) {
        dispatch(
          navigateVanLoadActions.setBagCountUpdateStatus({
            orderId: orderId,
            isBagCountUpdated: true,
          }),
        );
        dispatch(
          navigateVanLoadActions.setUsedBagCount({
            orderId: orderId,
            usedBag: orderRes?.bagsUsed,
          }),
        );
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  return {addBagsUsersForOrder, loading};
}

export function useCheckMasterRun(): [
  (
    runId: number,
    callBack: (status: DeliveryLocationStatus) => Promise<void>,
  ) => Promise<void>,
  ApiState<CheckMasterRun>,
] {
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const [state, setState] = useState<ApiState<CheckMasterRun>>({
    loading: false,
    error: undefined,
    data: undefined,
  });

  const func = useCallback(
    async (
      runId: number,
      callBack: (status: DeliveryLocationStatus) => Promise<void>,
    ) => {
      if (!runId) return;
      setState({...state, loading: true});
      try {
        const response = await gqlService?.mutation<CheckMasterRunResponse>({
          mutation: CHECK_MASTER_RUN,
          variables: {
            runId: runId,
          },
          fetchPolicy: 'network-only',
        });
        const res = response?.data?.getRun;
        if (res) {
          setState({
            ...state,
            data: res,
            loading: false,
          });
          callBack(
            res.runConfigId
              ? DeliveryLocationStatus.awaitingHandover
              : DeliveryLocationStatus.driverAssigned,
          );
        }
      } catch (error: any) {
        setState({...state, loading: false});
        showErrorToast(error?.message);
      }
    },
    [showErrorToast, state],
  );

  return [func, state];
}
